import { z } from "zod";
/**
 * Query fan-out: the searches an engine would run behind one question.
 *
 * An AI answer is almost never the result of one search. Google says so in its
 * own words — AI Mode "interprets the question with a large language model and
 * then starts Googling", firing themed sub-searches in parallel — and ChatGPT
 * discloses its searches in the transcript. The question a customer measures is
 * therefore not the question the engine actually asks the web, and this tool
 * exists to show the gap.
 *
 * **Modelled, not measured.** Every row here is a language model's answer to
 * "what would this engine search?", and nothing on this boundary may pretend
 * otherwise (AGENTS.md §14a). Two consequences are built into the types rather
 * than left to the screens:
 *
 *  1. There is nowhere to put a result. A predicted query has text, a kind, an
 *     intent and a reason — no citation, no brand, no rank, no answer. There is
 *     no route by which a prediction can become a recorded observation.
 *  2. It is kept apart from the real thing. `fanout_queries` holds the searches
 *     an engine *disclosed* during a capture we actually ran. That table is
 *     evidence. These are studies, they live in their own tables under their
 *     own name, and no query joins the two.
 *
 * What a person may do with a prediction is adopt it: pick the ones worth
 * watching and put them in the library as ordinary questions. From that moment
 * they are captured and scored like anything else, and their origin buys them
 * no special standing.
 */
/**
 * The three answer surfaces this tool models.
 *
 * Separate rather than one "Google" because they fan out differently and a
 * customer's work changes accordingly. AI Mode fans out widest — Google
 * describes dozens to hundreds of background searches for a hard question. An
 * AI Overview sits on top of an ordinary result page and stays much closer to
 * what was typed. ChatGPT runs a handful of searches and shows them to you.
 *
 * Stored as a string rather than a Postgres enum. A fourth surface is a data
 * change, and an enum makes it a migration that locks the table (§7).
 */
export const fanoutModeSchema = z.enum([
    "chatgpt",
    "google_ai_mode",
    "google_ai_overview",
]);
/**
 * What kind of move a predicted query makes away from the original.
 *
 * A closed vocabulary, and it is closed on purpose: free text here would give
 * every study its own private taxonomy, and the column exists precisely so a
 * reader can filter "show me the comparisons". The seven come from how the
 * engines are documented to expand a query, and each names a different way the
 * search leaves the words that were typed:
 *
 * - `reformulation` — the same question in different words. Synonyms, a noun
 *   swapped for a verb. Same intent, different index terms.
 * - `related` — an adjacent question a person asking this would also ask.
 * - `implicit` — a question they did *not* ask that the answer needs anyway.
 *   The engine anticipates the follow-up and answers it pre-emptively.
 * - `comparison` — head-to-head, fired when the engine reads buying intent.
 * - `entity` — a search for a specific named thing the topic implies.
 * - `freshness` — the same question aimed at recency: this year, latest, new.
 * - `personalised` — narrowed by who is asking: a place, a team size, a budget.
 *
 * `personalised` is spelled the way the rest of this codebase spells it.
 */
export const fanoutQueryKindSchema = z.enum([
    "reformulation",
    "related",
    "implicit",
    "comparison",
    "entity",
    "freshness",
    "personalised",
]);
/**
 * One predicted search.
 *
 * `predicted`, and the name is deliberate: a search an engine actually
 * disclosed is a fan-out query — read from its own text before the browser
 * workers went, and never mixed with a prediction in a metric.
 */
export const predictedQuerySchema = z.object({
    id: z.string(),
    /** As predicted, in the order the model put them. */
    position: z.number().int().min(0),
    /** The search itself, written the way a search is written rather than asked. */
    query: z.string(),
    kind: fanoutQueryKindSchema,
    /** What the person behind this search wants. One sentence. */
    intent: z.string(),
    /** Why the engine would run it. One sentence, and it is the model's, not ours. */
    reasoning: z.string(),
    /**
     * Whether this workspace's library already holds this question.
     *
     * Computed on read by matching the normalised text, not stored. A study is a
     * snapshot of a prediction; whether the library has caught up with it changes
     * every time somebody adds a prompt, and a stored copy would go stale and
     * then quietly offer to add a duplicate.
     */
    inLibrary: z.boolean(),
});
/** One engine's whole prediction for one question. */
export const fanoutModeResultSchema = z.object({
    mode: fanoutModeSchema,
    /**
     * Why this many queries, in the model's words.
     *
     * Shown above the table because the count is the first thing a reader
     * questions. Fifteen queries with no explanation reads as a setting; fifteen
     * queries with "the question has four distinct facets" reads as a judgement,
     * which is what it is.
     */
    reasoning: z.string(),
    /** Which model produced it. A prediction is only as good as its author. */
    model: z.string(),
    queries: z.array(predictedQuerySchema),
});
/** One run of the tool: one question, across every mode that was asked for. */
export const fanoutStudySchema = z.object({
    id: z.string(),
    query: z.string(),
    createdAt: z.string(),
    modes: z.array(fanoutModeResultSchema),
});
/** A row in the run history. The counts, not the queries. */
export const fanoutStudySummarySchema = z.object({
    id: z.string(),
    query: z.string(),
    /** Predicted queries across every mode — what the history column counts. */
    queryCount: z.number().int().min(0),
    modes: z.array(fanoutModeSchema),
    createdAt: z.string(),
});
export const fanoutStudyListSchema = z.object({
    studies: z.array(fanoutStudySummarySchema),
});
/**
 * What to model, and where.
 *
 * At least one mode: a study of nothing is a bill for nothing. Every mode is a
 * separate model call, and the screen says so before the button is pressed.
 */
/**
 * What came back, including what did not.
 *
 * A study plus the surfaces that failed, rather than a study alone. Each mode
 * is its own model call, so two of three succeeding is a normal outcome — and a
 * screen handed only the two would show a tab bar with a gap in it and no way
 * to say why. Named failures let it say "AI Mode could not be modelled" instead
 * of quietly measuring less than the customer asked for.
 */
export const fanoutStudyResultSchema = z.object({
    study: fanoutStudySchema,
    failures: z.array(z.object({
        mode: fanoutModeSchema,
        /** A short stable code — never an upstream provider's own prose (§5). */
        reason: z.string(),
    })),
});
export const createFanoutStudySchema = z.object({
    query: z.string().trim().min(3).max(300),
    modes: z.array(fanoutModeSchema).min(1).max(3),
});
/**
 * Adopting predictions: put these questions in the library, on this report.
 *
 * Texts rather than prediction ids. A person may edit a predicted query before
 * adopting it — they should, most of them are written as searches rather than
 * as questions — and an id would silently save the unedited original.
 */
export const adoptPromptsSchema = z.object({
    reportId: z.string().min(1),
    texts: z.array(z.string().trim().min(3).max(500)).min(1).max(100),
});
/**
 * What happened to each one, individually.
 *
 * Not a count. Adopting twenty questions where three name a brand, two are
 * already in the library and one is over quota is six different outcomes, and
 * "14 added" hides every one of them. The screen shows this list back.
 */
export const adoptOutcomeSchema = z.object({
    text: z.string(),
    outcome: z.enum([
        /** Created in the library and put on the report. */
        "added",
        /** Already in the library; the report now reads it too. */
        "linked",
        /** Already in the library and already on this report. Nothing changed. */
        "already",
        /** Refused, and `problem` says why. Nothing was created. */
        "refused",
    ]),
    /** Present only on `refused`, and always present then. */
    problem: z.string().nullable(),
    /** The library id, once there is one. Null on `refused`. */
    promptId: z.string().nullable(),
});
export const adoptResultSchema = z.object({
    results: z.array(adoptOutcomeSchema),
    /** Questions on the report afterwards — the total, so the screen need not add up. */
    promptCount: z.number().int().min(0),
});
//# sourceMappingURL=fanout.js.map